/**
 * Maps human-readable keys to translation IDs for the membership page.
 * Used by `app/(frontend)/[country]/[locale]/(default-nav)/membership/membership-client.tsx`
 * to look up translations from `messages/{locale}.json` under the
 * `_membership` namespace.
 *
 * The membership page is new (no Framer XLIFF export yet), so keys are
 * semantic snake_case rather than XLIFF unit IDs. If a Framer source-of-
 * truth ever lands, swap the values here for the unit IDs and re-run
 * `scripts/xliff-to-json.ts` — the keys.ts contract stays the same.
 */
export const membershipKeys = {
  // Hero
  heroPill: "membership_hero_pill",
  heroHeadingLine1: "membership_hero_heading_line_1",
  heroHeadingLine2: "membership_hero_heading_line_2",
  heroSubtitle: "membership_hero_subtitle",
  heroCtaPrimary: "membership_hero_cta_primary",
  heroCtaSecondary: "membership_hero_cta_secondary",
  heroKpi1Value: "membership_hero_kpi_1_value",
  heroKpi1Label: "membership_hero_kpi_1_label",
  heroKpi2Value: "membership_hero_kpi_2_value",
  heroKpi2Label: "membership_hero_kpi_2_label",
  heroKpi3Value: "membership_hero_kpi_3_value",
  heroKpi3Label: "membership_hero_kpi_3_label",

  // Trust logo strip
  trustTagPartnerWith: "membership_trust_tag_partner_with",
  trustTagExperienceAt: "membership_trust_tag_experience_at",
  trustTagAsSeenOn: "membership_trust_tag_as_seen_on",

  // Why continuous (Solving the gap)
  whyLabel: "membership_why_label",
  whyHeadingLead: "membership_why_heading_lead",
  whyHeadingAccent: "membership_why_heading_accent",
  whyStatement1: "membership_why_statement_1",
  whyStatement2: "membership_why_statement_2",
  whyStatement3: "membership_why_statement_3",

  // Included grid
  includedLabel: "membership_included_label",
  includedHeading: "membership_included_heading",
  includedItem1: "membership_included_item_1",
  includedItem2: "membership_included_item_2",
  includedItem2Sub: "membership_included_item_2_sub",
  includedItem3: "membership_included_item_3",
  includedItem4: "membership_included_item_4",
  includedItem5: "membership_included_item_5",
  includedItem5Sub: "membership_included_item_5_sub",
  includedItem6: "membership_included_item_6",
  includedItem6Sub: "membership_included_item_6_sub",

  // Membership year (timeline)
  yearLabel: "membership_year_label",
  yearHeading: "membership_year_heading",
  yearDescription: "membership_year_description",
  yearProgressDotAria: "membership_year_progress_dot_aria",
  journey1Badge: "membership_journey_1_badge",
  journey1Title: "membership_journey_1_title",
  journey1Desc: "membership_journey_1_desc",
  journey2Badge: "membership_journey_2_badge",
  journey2Title: "membership_journey_2_title",
  journey2Desc: "membership_journey_2_desc",
  journey2Bullet1: "membership_journey_2_bullet_1",
  journey2Bullet2: "membership_journey_2_bullet_2",
  journey2Bullet3: "membership_journey_2_bullet_3",
  journey3Badge: "membership_journey_3_badge",
  journey3Title: "membership_journey_3_title",
  journey3Desc: "membership_journey_3_desc",
  journey4Badge: "membership_journey_4_badge",
  journey4Title: "membership_journey_4_title",
  journey4Desc: "membership_journey_4_desc",
  journey5Badge: "membership_journey_5_badge",
  journey5Title: "membership_journey_5_title",
  journey5Desc: "membership_journey_5_desc",
  journey5Bullet1: "membership_journey_5_bullet_1",
  journey5Bullet2: "membership_journey_5_bullet_2",
  journey5Bullet3: "membership_journey_5_bullet_3",
  journeyCtaMember: "membership_journey_cta_member",
  journeyCtaReport: "membership_journey_cta_report",

  // Locations strip
  locationsLabel: "membership_locations_label",
  locationsHeading: "membership_locations_heading",
  locationsDescription: "membership_locations_description",
  locationsSoonLabel: "membership_locations_soon_label",
  locationsSoonCities: "membership_locations_soon_cities",
  locationsLegendActive: "membership_locations_legend_active",
  locationsCheckupNote: "membership_locations_checkup_note",
  locationsCta: "membership_locations_cta",

  // Frequency picker (Cadence)
  frequencyLabel: "membership_frequency_label",
  frequencyHeading: "membership_frequency_heading",
  frequencyDescription: "membership_frequency_description",
  frequencyBaselineName: "membership_frequency_baseline_name",
  frequencyBaselineTests: "membership_frequency_baseline_tests",
  frequencyBaselineDesc: "membership_frequency_baseline_desc",
  frequency2xName: "membership_frequency_2x_name",
  frequency2xTests: "membership_frequency_2x_tests",
  frequency2xDesc: "membership_frequency_2x_desc",
  frequency4xName: "membership_frequency_4x_name",
  frequency4xTests: "membership_frequency_4x_tests",
  frequency4xDesc: "membership_frequency_4x_desc",
  frequencyPerYearSuffix: "membership_frequency_per_year_suffix",
  frequencyCta: "membership_frequency_cta",
  frequencySavings: "membership_frequency_savings",
  frequencyBillingNote: "membership_frequency_billing_note",

  // Blood test browser (What we test for)
  testLabel: "membership_test_label",
  testHeading: "membership_test_heading",
  testDescription: "membership_test_description",
  testTabBaseline: "membership_test_tab_baseline",
  testTabFollowup: "membership_test_tab_followup",
  testFollowupNote: "membership_test_followup_note",
  testSearchAria: "membership_test_search_aria",
  testSearchPlaceholder: "membership_test_search_placeholder",
  testSearchClearAria: "membership_test_search_clear_aria",
  testNoMatches: "membership_test_no_matches",
  testNoMarkers: "membership_test_no_markers",
  testLearnMore: "membership_test_learn_more",
  testSeeAll: "membership_test_see_all",

  // Action plan preview
  planLabel: "membership_plan_label",
  planHeading: "membership_plan_heading",
  planDescription: "membership_plan_description",
  planBullet1: "membership_plan_bullet_1",
  planBullet2: "membership_plan_bullet_2",
  planBullet3: "membership_plan_bullet_3",
  planCta: "membership_plan_cta",
  planImageAlt: "membership_plan_image_alt",

  // Testimonials section
  testimonialsHeading: "membership_testimonials_heading",
  testimonialsDescription: "membership_testimonials_description",

  // Member pricing
  memberPricingLabel: "membership_member_pricing_label",
  memberPricingHeading: "membership_member_pricing_heading",
  memberPricingDescription: "membership_member_pricing_description",
  memberPricingShowAll: "membership_member_pricing_show_all",
  memberPricingHideAll: "membership_member_pricing_hide_all",
  memberPricingCaption: "membership_member_pricing_caption",
  memberPricingColService: "membership_member_pricing_col_service",
  memberPricingColRegular: "membership_member_pricing_col_regular",
  memberPricingColMember: "membership_member_pricing_col_member",
  memberPricingColSave: "membership_member_pricing_col_save",
  memberPricingFootnote: "membership_member_pricing_footnote",
  memberPricingCardSave: "membership_member_pricing_card_save",
  memberPricingItemMriCore: "membership_member_pricing_item_mri_core",
  memberPricingItemMriAdvanced: "membership_member_pricing_item_mri_advanced",
  memberPricingItemMriPro: "membership_member_pricing_item_mri_pro",
  memberPricingItemFollowupBlood:
    "membership_member_pricing_item_followup_blood",
  memberPricingItemAdvancedBlood:
    "membership_member_pricing_item_advanced_blood",
  memberPricingItemHormone: "membership_member_pricing_item_hormone",
  memberPricingItemOmega: "membership_member_pricing_item_omega",
  memberPricingItemBVitamins: "membership_member_pricing_item_b_vitamins",
  memberPricingItemBodyAi: "membership_member_pricing_item_body_ai",
  memberPricingItemDexa: "membership_member_pricing_item_dexa",
  memberPricingItemLpa: "membership_member_pricing_item_lpa",
  memberPricingItemPsa: "membership_member_pricing_item_psa",
  memberPricingItemCortisol: "membership_member_pricing_item_cortisol",

  // Editorial blocks (member pricing carousel)
  editorialMriTitle: "membership_editorial_mri_title",
  editorialMriSubtitle: "membership_editorial_mri_subtitle",
  editorialBVitaminsTitle: "membership_editorial_b_vitamins_title",
  editorialBVitaminsSubtitle: "membership_editorial_b_vitamins_subtitle",
  editorialHormoneTitle: "membership_editorial_hormone_title",
  editorialHormoneSubtitle: "membership_editorial_hormone_subtitle",
  editorialOmegaTitle: "membership_editorial_omega_title",
  editorialOmegaSubtitle: "membership_editorial_omega_subtitle",

  // Membership vs single test (compare table)
  compareLabel: "membership_compare_label",
  compareHeading: "membership_compare_heading",
  compareDescription: "membership_compare_description",
  compareCaption: "membership_compare_caption",
  compareColFeature: "membership_compare_col_feature",
  compareColMembers: "membership_compare_col_members",
  compareColSingle: "membership_compare_col_single",
  compareAriaIncluded: "membership_compare_aria_included",
  compareAriaNotIncluded: "membership_compare_aria_not_included",
  compareRow1Feature: "membership_compare_row_1_feature",
  compareRow1MemberText: "membership_compare_row_1_member_text",
  compareRow1MemberNote: "membership_compare_row_1_member_note",
  compareRow1SingleText: "membership_compare_row_1_single_text",
  compareRow2Feature: "membership_compare_row_2_feature",
  compareRow3Feature: "membership_compare_row_3_feature",
  compareRow4Feature: "membership_compare_row_4_feature",
  compareRow5Feature: "membership_compare_row_5_feature",
  compareRow6Feature: "membership_compare_row_6_feature",
  compareRow7Feature: "membership_compare_row_7_feature",
  compareRow8Feature: "membership_compare_row_8_feature",
  compareRow8Tag: "membership_compare_row_8_tag",

  // Clinical team
  teamLabel: "membership_team_label",
  teamHeading: "membership_team_heading",
  teamDescription: "membership_team_description",
  teamCta: "membership_team_cta",
  expert1Title: "membership_expert_1_title",
  expert2Title: "membership_expert_2_title",
  expert3Title: "membership_expert_3_title",
  expert4Title: "membership_expert_4_title",
  expert5Title: "membership_expert_5_title",
  expert6Title: "membership_expert_6_title",

  // FAQ
  faqHeading: "membership_faq_heading",
  faqLink: "membership_faq_link",

  // ─── Decorative overlays inside marketing illustrations ────────────────
  // These are the small glass cards / fake notifications / mock UI panels
  // layered on top of the journey timeline images, the action-plan photo,
  // and the "What's included" grid. They render real on-page text that
  // every locale must see localized — they are NOT just for the markdown
  // mirror.

  // Biomarker card / overlay (BiomarkerCard, BiomarkerOverlay,
  // HealthReportCard biomarker widget).
  overlayBiomarkerLabel: "membership_overlay_biomarker_label",
  overlayBiomarkerLabelPlural: "membership_overlay_biomarker_label_plural",
  overlayBiomarkerChipOptimal: "membership_overlay_biomarker_chip_optimal",
  overlayBiomarkerChipNormal: "membership_overlay_biomarker_chip_normal",
  overlayBiomarkerChipOutOfRange:
    "membership_overlay_biomarker_chip_out_of_range",

  // CadenceOverlay
  overlayCadenceBaselineName: "membership_overlay_cadence_baseline_name",
  overlayCadenceBaselineDesc: "membership_overlay_cadence_baseline_desc",
  overlayCadence2xName: "membership_overlay_cadence_2x_name",
  overlayCadence2xDesc: "membership_overlay_cadence_2x_desc",
  overlayCadence4xName: "membership_overlay_cadence_4x_name",
  overlayCadence4xDesc: "membership_overlay_cadence_4x_desc",

  // ActionPlanCard (compact "card on a tile")
  overlayActionCalciumTitle: "membership_overlay_action_calcium_title",
  overlayActionCalciumDesc: "membership_overlay_action_calcium_desc",
  overlayActionExerciseTitle: "membership_overlay_action_exercise_title",
  overlayActionExerciseDesc: "membership_overlay_action_exercise_desc",
  overlayActionPotassiumTitle: "membership_overlay_action_potassium_title",
  overlayActionPotassiumDesc: "membership_overlay_action_potassium_desc",

  // MemberPricingOverlay
  overlayMemberPricingHeading: "membership_overlay_member_pricing_heading",
  overlayMemberPricingMri: "membership_overlay_member_pricing_mri",
  overlayMemberPricingHeartCt: "membership_overlay_member_pricing_heart_ct",

  // LabUploadOverlay / shared "Within range" pill
  overlayLabUploaded: "membership_overlay_lab_uploaded",
  overlayWithinRange: "membership_overlay_within_range",

  // HealthReportCard
  overlayHealthReportTitle: "membership_overlay_health_report_title",
  overlayHealthBiologicalAge: "membership_overlay_health_biological_age",
  overlayHealthBiologicalAgeBody:
    "membership_overlay_health_biological_age_body",
  overlayHealthHeartRiskScore: "membership_overlay_health_heart_risk_score",
  overlayHealthHeartRiskBody: "membership_overlay_health_heart_risk_body",

  // ProgressNotificationFull / Collapsed
  overlayProgressBrand: "membership_overlay_progress_brand",
  overlayProgressBody: "membership_overlay_progress_body",
  overlayProgressUpdated: "membership_overlay_progress_updated",

  // ExpertSupportOverlay (chat bubbles)
  overlayExpertBubble1: "membership_overlay_expert_bubble_1",
  overlayExpertBubble2: "membership_overlay_expert_bubble_2",

  // DoctorMessageBubble (attributed quote)
  overlayDoctorMessage: "membership_overlay_doctor_message",

  // FastingGlucoseTrendCard
  overlayTrendChartTitle: "membership_overlay_trend_chart_title",
  overlayTrendLegendIntermediate:
    "membership_overlay_trend_legend_intermediate",
  overlayTrendLegendNormalRange: "membership_overlay_trend_legend_normal_range",
  overlayTrendExplainerHeading: "membership_overlay_trend_explainer_heading",
  overlayTrendExplainerBody: "membership_overlay_trend_explainer_body",

  // AppointmentCard
  overlayApptAddons: "membership_overlay_appt_addons",
  overlayApptDate: "membership_overlay_appt_date",
  overlayApptTime: "membership_overlay_appt_time",
  overlayApptConfirmed: "membership_overlay_appt_confirmed",
  overlayApptGetDirections: "membership_overlay_appt_get_directions",
  overlayApptAddToCalendar: "membership_overlay_appt_add_to_calendar",

  // PersonalPlanCard
  overlayPlanTitle: "membership_overlay_plan_title",
  overlayPlanFilterAll: "membership_overlay_plan_filter_all",
  overlayPlanFilterFollowups: "membership_overlay_plan_filter_followups",
  overlayPlanFilterExercise: "membership_overlay_plan_filter_exercise",
  overlayPlanFilterNutrition: "membership_overlay_plan_filter_nutrition",
  overlayPlanFilterLifestyle: "membership_overlay_plan_filter_lifestyle",
  overlayPlanCalciumTitle: "membership_overlay_plan_calcium_title",
  overlayPlanCalciumDesc: "membership_overlay_plan_calcium_desc",
  overlayPlanExerciseTitle: "membership_overlay_plan_exercise_title",
  overlayPlanExerciseDesc: "membership_overlay_plan_exercise_desc",
  overlayPlanPotassiumTitle: "membership_overlay_plan_potassium_title",
  overlayPlanPotassiumDesc: "membership_overlay_plan_potassium_desc",
  overlayPlanChipImpactAreas2: "membership_overlay_plan_chip_impact_areas_2",
  overlayPlanChipImpactAreas3: "membership_overlay_plan_chip_impact_areas_3",
  overlayPlanChipNutrition: "membership_overlay_plan_chip_nutrition",
  overlayPlanChipExercise: "membership_overlay_plan_chip_exercise",
  overlayPlanChipKidney: "membership_overlay_plan_chip_kidney",
  overlayPlanChipDaily: "membership_overlay_plan_chip_daily",
  overlayPlanChipWeekly: "membership_overlay_plan_chip_weekly",
  overlayPlanChipDueWeeks: "membership_overlay_plan_chip_due_weeks",
  overlayPlanBtnViewDetails: "membership_overlay_plan_btn_view_details",
  overlayPlanBtnBookTest: "membership_overlay_plan_btn_book_test",

  // Final CTA
  finalHeading: "membership_final_heading",
  finalPricePrefix: "membership_final_price_prefix",
  finalPriceSuffix: "membership_final_price_suffix",
  finalPillar1: "membership_final_pillar_1",
  finalPillar2: "membership_final_pillar_2",
  finalPillar3: "membership_final_pillar_3",
  finalFeature1: "membership_final_feature_1",
  finalFeature2: "membership_final_feature_2",
  finalFeature3: "membership_final_feature_3",
  finalFeature4: "membership_final_feature_4",
  finalFeature5: "membership_final_feature_5",
  finalFeature6: "membership_final_feature_6",
} as const
